/**
 * Admin Dashboard Component - Detachable Admin Module
 *
 * Direct user management interface without extra navigation.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import QuickActionsModal from './QuickActionsModal';
import BulkActionsModal from './BulkActionsModal';
import UserListView from './UserListView';
import { useAdminData } from '../hooks/useAdminData';
import { debounce } from '../utils';
import type { PlatformUser, UserSortField, SortOrder } from '../types';
import { Button, Input, Select } from '../../components';
import { Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { adminApi, type RunningApp } from '../services/adminApi';
import { appLifecycleApi } from '../../services/appLifecycleApi';
import { Pagination } from '../../components/Pagination';
import { type PageSize, calcTotalPages } from '../../utils/pagination';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

type AdminTab = 'userDetails' | 'runningApps';

// ─────────────────────────────────────────────────────────────
// UserSearchAndFilters sub-component
// Mirrors the SearchAndFilters pattern from Dashboard.tsx:
//  • localSearch for instant UI feedback
//  • stable debouncedRef so the timer never resets on re-render
//  • onSearchChangeRef to avoid stale closures inside the debounce
// ─────────────────────────────────────────────────────────────

interface UserSearchAndFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  enabled: boolean | undefined;
  onEnabledChange: (value: boolean | undefined) => void;
  planType: string | undefined;
  onPlanTypeChange: (value: string | undefined) => void;
}

const UserSearchAndFilters: React.FC<UserSearchAndFiltersProps> = ({
  searchQuery,
  onSearchChange,
  enabled,
  onEnabledChange,
  planType,
  onPlanTypeChange,
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showFilters, setShowFilters] = useState(false);

  // Keep callback ref stable so the debounce never captures a stale closure
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => { onSearchChangeRef.current = onSearchChange; }, [onSearchChange]);

  // Create debounce exactly once — lazy ref initialization (same as Dashboard.tsx)
  const debouncedRef = useRef<ReturnType<typeof debounce>>();
  if (!debouncedRef.current) {
    debouncedRef.current = debounce((value: string) => {
      onSearchChangeRef.current(value);
    }, 300);
  }

  // Sync localSearch if the parent resets searchQuery (e.g. "Clear filters")
  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value);          // immediate — input feels instant
    debouncedRef.current?.(value);  // deferred — triggers actual filtering
  }, []);

  const handleHideFilters = () => {
    onEnabledChange(undefined);
    onPlanTypeChange(undefined);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setShowFilters(false);
  };

  return (
    <div className="bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm p-bg-v2-3xl">
      {/* Search Bar */}
      <div className={`flex items-center space-x-bg-v2-xl${showFilters ? ' mb-bg-v2-xl' : ''}`}>
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search users by email, name, or username..."
            value={localSearch}
            onChange={handleSearchChange}
          />
        </div>
        <Button
          label={showFilters ? 'Hide Filters' : 'Show Filters'}
          variant={showFilters ? 'brand' : 'outline'}
          size="medium"
          onClick={showFilters ? handleHideFilters : () => setShowFilters(true)}
        />
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-bg-v2-xl pt-bg-v2-xl border-t border-border-secondary">
          <Select
            label="Account Status"
            options={[
              { value: '', label: 'All Status' },
              { value: 'true', label: 'Enabled' },
              { value: 'false', label: 'Disabled' },
            ]}
            value={enabled === undefined ? '' : enabled.toString()}
            onChange={(v) => onEnabledChange(v === '' ? undefined : v === 'true')}
          />
          <div className="flex flex-col gap-bg-v2-xs">
            <Select
              label="Plan Type"
              options={[
                { value: '', label: 'All Plans' },
                { value: 'starter', label: 'Starter' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'unlimited', label: 'Unlimited' },
              ]}
              value={planType || ''}
              onChange={(v) => onPlanTypeChange(v ? String(v) : undefined)}
            />
            {planType && (
              <p className="text-xs text-text-tertiary">
                Filters within the current page only. Change page to see more.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// RunningAppsView sub-component
// ─────────────────────────────────────────────────────────────

interface RunningAppsViewProps {
  apps: RunningApp[];
  loading: boolean;
  stoppingApps: Record<string, boolean>;
  onStop: (projectId: string) => void;
}

const RunningAppsView: React.FC<RunningAppsViewProps> = ({
  apps,
  loading,
  stoppingApps,
  onStop,
}) => {
  // localSearch updates instantly (input feels responsive).
  // debouncedSearch is what actually drives filtering (300ms delay).
  // Same pattern as UserSearchAndFilters.
  const [localSearch, setLocalSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const debouncedSearchRef = useRef(setDebouncedSearch);
  useEffect(() => { debouncedSearchRef.current = setDebouncedSearch; }, []);

  const debouncedRef = useRef<ReturnType<typeof debounce>>();
  if (!debouncedRef.current) {
    debouncedRef.current = debounce((value: string) => {
      debouncedSearchRef.current(value);
    }, 300);
  }

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedRef.current?.(value);
  }, []);

  const filtered = debouncedSearch.trim()
    ? apps.filter(
        (a) =>
          a.project_title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          a.owner_email.toLowerCase().includes(debouncedSearch.toLowerCase()),
      )
    : apps;

  return (
    <div className="space-y-bg-v2-3xl">
      {/* Search bar — same card style as User Details */}
      <div className="bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm p-bg-v2-3xl">
        <Input
          type="text"
          placeholder="Search by project name or owner email..."
          value={localSearch}
          onChange={handleSearchChange}
        />
      </div>

    <div className="bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table className="w-full divide-y divide-border-secondary table-fixed" style={{ minWidth: '500px' }}>
          <colgroup>
            <col className="w-10" />
            <col />
            <col />
            <col className="w-[80px]" />
          </colgroup>

          <thead className="bg-bg-secondary">
            <tr>
              <th className="px-bg-v2-md py-bg-v2-lg text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">
                #
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Project
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Owner
              </th>
              <th className="px-bg-v2-xl py-bg-v2-lg text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>

          {loading ? (
            <tbody className="bg-bg-primary divide-y divide-border-secondary">
              {[...Array(3)].map((_, i) => (
                <tr
                  key={i}
                  className="opacity-0"
                  style={{ animation: `fadeIn 0.3s ease-out ${i * 0.08}s forwards` }}
                >
                  <td className="px-bg-v2-md py-bg-v2-xl text-center">
                    <div className="w-4 h-4 skeleton-shimmer rounded mx-auto" />
                  </td>
                  <td className="px-bg-v2-xl py-bg-v2-xl">
                    <div className="h-4 skeleton-shimmer rounded w-40" />
                  </td>
                  <td className="px-bg-v2-xl py-bg-v2-xl">
                    <div className="h-4 skeleton-shimmer rounded w-48" />
                  </td>
                  <td className="px-bg-v2-xl py-bg-v2-xl text-center">
                    <div className="w-6 h-6 skeleton-shimmer rounded-full mx-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody className="bg-bg-primary divide-y divide-border-secondary">
              {filtered.map((app, index) => {
                const isStopping = stoppingApps[app.project_id] ?? false;
                return (
                  <tr
                    key={app.project_id}
                    className="hover:bg-bg-secondary opacity-0"
                    style={{ animation: `fadeIn 0.35s ease-out ${index * 0.05}s forwards` }}
                  >
                    <td className="px-bg-v2-md py-bg-v2-xl text-center text-sm text-text-tertiary">
                      {index + 1}
                    </td>
                    <td className="px-bg-v2-xl py-bg-v2-xl text-sm font-medium text-text-primary truncate">
                      {app.project_title}
                    </td>
                    <td className="px-bg-v2-xl py-bg-v2-xl text-sm text-text-tertiary truncate">
                      {app.owner_email}
                    </td>
                    <td className="px-bg-v2-xl py-bg-v2-xl text-center">
                      <Button
                        label={
                          <Icon
                            name={isStopping ? IconName.Loading : IconName.Stop}
                            size="sm"
                            className="text-fg-secondary"
                            spin={isStopping}
                            inheritColor
                          />
                        }
                        variant="ghost"
                        size="small"
                        title={isStopping ? 'Stopping...' : 'Stop App'}
                        className="!rounded-full !p-bg-v2-lg"
                        disabled={isStopping}
                        onClick={() => onStop(app.project_id)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      {!loading && filtered.length === 0 && (
        <div className="text-center py-bg-v2-6xl">
          <p className="text-text-tertiary">
            {debouncedSearch.trim() ? 'No applications match your search' : 'No running applications found'}
          </p>
          {debouncedSearch.trim() && (
            <button
              onClick={() => { setLocalSearch(''); setDebouncedSearch(''); }}
              className="mt-bg-v2-md text-sm text-text-brand-primary hover:text-text-brand-secondary font-medium"
            >
              Clear search
            </button>
          )}
        </div>
      )}
    </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main AdminDashboard component
// ─────────────────────────────────────────────────────────────

export const AdminDashboard: React.FC = () => {
  const { users } = useAdminData();

  // ── Tab state ─────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<AdminTab>('userDetails');

  // ── User Details tab state ─────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [planType, setPlanType] = useState<string | undefined>(undefined);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  // Sort state (server-side). Default to newest-first by created date so the
  // list opens in a predictable order; clicking a header switches columns.
  const [sortBy, setSortBy] = useState<UserSortField | undefined>('created_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Pagination state (server-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(25);

  const [quickAction, setQuickAction] = useState<{
    user: PlatformUser | null;
    action: 'addCredits' | 'changePlan' | null;
  }>({ user: null, action: null });

  const [bulkAction, setBulkAction] = useState<'addCredits' | 'changePlan' | null>(null);

  const tableRef = useRef<HTMLDivElement>(null);

  // ── Running Apps tab state ─────────────────────────────────
  const [runningApps, setRunningApps] = useState<RunningApp[]>([]);
  const [loadingRunningApps, setLoadingRunningApps] = useState(false);
  const [runningAppsError, setRunningAppsError] = useState<string | null>(null);
  const [stoppingApps, setStoppingApps] = useState<Record<string, boolean>>({});

  // Refs for per-app stop polling timers (keyed by project_id)
  const stopPollTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // mountedRef — guards all stop-poll state updates after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Server-side fetch — fires on page / pageSize / server-side filter changes
  // searchQuery is already debounced by UserSearchAndFilters before reaching here.
  // enabled is also a server-side param; planType stays client-side.
  useEffect(() => {
    users.loadUsers({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      search: searchQuery || undefined,
      enabled,
      // Only send sort params when a column is chosen; otherwise the backend
      // keeps its default (fast, paginated) path. axios omits undefined params.
      sort_by: sortBy,
      sort_order: sortBy ? sortOrder : undefined,
    });
  }, [page, pageSize, searchQuery, enabled, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll table into view when filters change
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchQuery, enabled, planType]);

  // ── Fetch running apps when tab becomes active ─────────────
  useEffect(() => {
    if (activeTab !== 'runningApps') return;

    const fetchRunningApps = async () => {
      setLoadingRunningApps(true);
      setRunningAppsError(null);
      try {
        const data = await adminApi.getRunningApps();
        setRunningApps(data);
      } catch (err: any) {
        setRunningAppsError(
          err?.response?.data?.detail || 'Failed to load running applications.'
        );
      } finally {
        setLoadingRunningApps(false);
      }
    };

    fetchRunningApps();
  }, [activeTab]);

  // Cleanup any lingering stop-poll timers on unmount
  useEffect(() => {
    return () => {
      Object.values(stopPollTimers.current).forEach(clearTimeout);
    };
  }, []);

  // ── Stop app handler ───────────────────────────────────────
  // Polling cadence matches useAppLifecycle exactly:
  //   • 20s (POLL_INTERVAL_TRANSITIONING) with ±10% jitter per tick
  //   • Pauses when the browser tab is hidden (visibilitychange)
  //   • 5-minute hard cutoff as a safety net
  const POLL_INTERVAL_MS = 20_000;
  const MAX_POLL_MS      = 5 * 60 * 1_000;
  const JITTER_RATIO     = 0.1;

  const jittered = (ms: number) => {
    const delta = ms * JITTER_RATIO;
    return Math.round(ms + (Math.random() * 2 - 1) * delta);
  };

  const handleStopApp = useCallback(async (projectId: string) => {
    if (stoppingApps[projectId]) return;

    setStoppingApps(prev => ({ ...prev, [projectId]: true }));

    try {
      await appLifecycleApi.stopApp(projectId);
    } catch {
      // Even if the stop call fails we still attempt status polling;
      // the button stays disabled and clears after the safety cutoff.
    }

    const startedAt = Date.now();
    let visibilityCleanup: (() => void) | null = null;

    const clearTimer = () => {
      if (stopPollTimers.current[projectId]) {
        clearTimeout(stopPollTimers.current[projectId]);
        delete stopPollTimers.current[projectId];
      }
    };

    const clearAll = () => {
      clearTimer();
      visibilityCleanup?.();
      visibilityCleanup = null;
    };

    const finish = () => {
      clearAll();
      if (!mountedRef.current) return;
      setRunningApps(prev => prev.filter(a => a.project_id !== projectId));
      setStoppingApps(prev => {
        const next = { ...prev };
        delete next[projectId];
        return next;
      });
    };

    const scheduleNext = (fn: () => void) => {
      clearTimer();
      if (document.hidden) return; // visibility handler will reschedule
      stopPollTimers.current[projectId] = setTimeout(fn, jittered(POLL_INTERVAL_MS));
    };

    const poll = async () => {
      if (!mountedRef.current) { clearAll(); return; }

      if (Date.now() - startedAt > MAX_POLL_MS) {
        // Safety cutoff — give up and re-enable the button
        clearAll();
        if (mountedRef.current) {
          setStoppingApps(prev => {
            const next = { ...prev };
            delete next[projectId];
            return next;
          });
        }
        return;
      }

      try {
        const { status } = await appLifecycleApi.getStatus(projectId);
        if (status === 'stopped' || status === 'no_compose') {
          finish();
          return;
        }
      } catch {
        // swallow and retry on next tick
      }

      scheduleNext(poll);
    };

    // Visibility handler: pause on hide, resume immediately on show
    const handleVisibility = () => {
      if (document.hidden) {
        clearTimer();
      } else {
        void poll(); // immediate poll, then reschedule inside poll()
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    visibilityCleanup = () =>
      document.removeEventListener('visibilitychange', handleVisibility);

    // First poll after 1s — mirrors the 1s kick in useAppLifecycle
    stopPollTimers.current[projectId] = setTimeout(poll, 1_000);
  }, [stoppingApps]);

  // ── Derived values ─────────────────────────────────────────
  // planType is client-side only (backend doesn't filter by plan); everything
  // else (search, enabled) is handled server-side by the effect above.
  const filteredUsers = (users.users || []).filter(
    user => !planType || user.billing.plan_type === planType,
  );

  const totalPages = calcTotalPages(users.totalCount, pageSize);

  // ── User Details handlers ──────────────────────────────────

  const handleSelectUser = (userId: string, checked: boolean) => {
    const next = new Set(selectedUsers);
    checked ? next.add(userId) : next.delete(userId);
    setSelectedUsers(next);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedUsers(
      checked
        ? new Set(filteredUsers.map(u => u.id || u.email))
        : new Set()
    );
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setEnabled(undefined);
    setPlanType(undefined);
    setPage(1);
  };

  // Reset to page 1 whenever a server-side filter changes externally
  // (e.g. from UserSearchAndFilters callbacks)
  const handleSearchChange = (value: string) => {
    setPage(1);
    setSearchQuery(value);
  };

  const handleEnabledChange = (value: boolean | undefined) => {
    setPage(1);
    setEnabled(value);
  };

  // Sort in the exact direction the user chose (clicking the ▲ caret → asc, ▼ → desc,
  // or the convenience order computed when the label is clicked). Always jump back to
  // page 1 so the new order starts fresh.
  const handleSort = (field: UserSortField, order: SortOrder) => {
    setPage(1);
    setSortBy(field);
    setSortOrder(order);
  };

  const handleQuickAction = (user: PlatformUser, action: 'addCredits' | 'changePlan') =>
    setQuickAction({ user, action });

  const handleQuickActionClose = () => setQuickAction({ user: null, action: null });
  const handleQuickActionSuccess = () => users.refreshUsers();

  const handleBulkActionClose = () => setBulkAction(null);
  const handleBulkActionSuccess = () => {
    users.refreshUsers();
    setSelectedUsers(new Set());
  };

  const hasActiveFilters = !!searchQuery || enabled !== undefined || !!planType;

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      {/*
        Outer shell: fills the AdminLayout content area as a flex column.
        Nothing here scrolls — scrolling happens inside each tab's content area.
      */}
      <div className="flex flex-col h-full overflow-hidden gap-bg-v2-3xl">

        {/* ── Tab buttons — always visible, never scrolls ──── */}
        <div className="flex-shrink-0 bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm">
          <div className="border-b border-border-secondary">
            <nav className="flex space-x-bg-v2-4xl px-bg-v2-3xl">
              {(
                [
                  { id: 'userDetails' as AdminTab, label: 'User Details' },
                  { id: 'runningApps' as AdminTab, label: 'Running Applications' },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-bg-v2-xl px-bg-v2-xs border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-border-brand-alt text-text-brand-primary'
                      : 'border-transparent text-text-tertiary hover:text-text-secondary hover:border-border-primary'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* ── User Details tab ─────────────────────────────── */}
        {activeTab === 'userDetails' && (
          <div className="flex flex-col flex-1 min-h-0 gap-bg-v2-3xl">

            {/* Search + Filters — pinned, never scrolls */}
            <div className="flex-shrink-0">
              <UserSearchAndFilters
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                enabled={enabled}
                onEnabledChange={handleEnabledChange}
                planType={planType}
                onPlanTypeChange={setPlanType}
              />
            </div>

            {/* Bulk Actions — pinned, conditional */}
            {selectedUsers.size > 0 && (
              <div className="flex-shrink-0 bg-bg-brand-primary border border-border-secondary rounded-bg-v2-lg p-bg-v2-xl">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-primary">
                    {selectedUsers.size} users selected
                  </span>
                  <div className="flex items-center space-x-bg-v2-lg">
                    <Button label="Add Credits" variant="success" size="small" onClick={() => setBulkAction('addCredits')} />
                    <Button label="Change Plan" variant="brand"   size="small" onClick={() => setBulkAction('changePlan')} />
                    <Button label="Cancel"      variant="outline" size="small" onClick={() => setSelectedUsers(new Set())} />
                  </div>
                </div>
              </div>
            )}

            {/* Error — pinned, conditional */}
            {users.error && (
              <div className="flex-shrink-0 bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-lg p-bg-v2-xl">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-fg-error-primary">⚠️</span>
                  </div>
                  <div className="ml-bg-v2-lg">
                    <h3 className="text-sm font-medium text-text-error-primary">Error Loading Users</h3>
                    <p className="mt-bg-v2-xs text-sm text-text-error-primary-hover">{users.error}</p>
                    <Button
                      label="Try Again"
                      variant="link"
                      size="small"
                      onClick={() => users.refreshUsers()}
                      className="mt-bg-v2-md text-text-error-primary hover:text-text-error-primary"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Table + pinned pagination — one card, fills remaining space */}
            {!users.error && (
              <div className="flex flex-col flex-1 min-h-0 bg-bg-primary rounded-bg-v2-lg shadow-bg-v2-sm overflow-hidden">
                {/* Scrollable table area */}
                <div ref={tableRef} className="flex-1 min-h-0 overflow-y-auto modern-scrollbar-spaced">
                  <UserListView
                    users={filteredUsers}
                    loading={users.loading}
                    selectedUsers={selectedUsers}
                    onSelectUser={handleSelectUser}
                    onSelectAll={handleSelectAll}
                    onQuickAction={handleQuickAction}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={handleClearFilters}
                    filterKey={`${searchQuery}-${enabled}-${planType}-${sortBy}-${sortOrder}`}
                    wrapperClassName="overflow-x-auto"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                </div>

                {/* Pagination — pinned at bottom of the card */}
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalItems={users.totalCount}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Running Applications tab ──────────────────────── */}
        {activeTab === 'runningApps' && (
          <div className="flex flex-col flex-1 min-h-0 overflow-y-auto modern-scrollbar-spaced">
            {runningAppsError && (
              <div className="flex-shrink-0 bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-lg p-bg-v2-xl">
                <div className="flex items-start gap-3">
                  <span className="text-fg-error-primary mt-0.5">⚠️</span>
                  <div>
                    <h3 className="text-sm font-medium text-text-error-primary">
                      Error Loading Running Applications
                    </h3>
                    <p className="mt-1 text-sm text-text-error-primary-hover">{runningAppsError}</p>
                    <Button
                      label="Try Again"
                      variant="link"
                      size="small"
                      onClick={() => setActiveTab('userDetails')}
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>
            )}

            {!runningAppsError && (
              <RunningAppsView
                apps={runningApps}
                loading={loadingRunningApps}
                stoppingApps={stoppingApps}
                onStop={handleStopApp}
              />
            )}
          </div>
        )}

      </div>

      {/* Modals rendered outside the scroll container so position:fixed works correctly */}
      <QuickActionsModal
        user={quickAction.user}
        action={quickAction.action}
        onClose={handleQuickActionClose}
        onSuccess={handleQuickActionSuccess}
      />

      <BulkActionsModal
        users={filteredUsers.filter(user => selectedUsers.has(user.id || user.email))}
        action={bulkAction}
        onClose={handleBulkActionClose}
        onSuccess={handleBulkActionSuccess}
      />
    </AdminLayout>
  );
};

export default AdminDashboard;
